import React, { useEffect, useRef } from 'react';

export default function SmokeParticles() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let animationFrameId;

        // Configuration
        const particleCount = 500;
        const particles = [];
        const mouse = { x: undefined, y: undefined, radius: 150 };

        // Colors for sustainability/nature (Very subtle)
        const colors = [
            'rgba(155, 176, 136, 0.4)',
            'rgba(227, 234, 224, 0.3)',
            'rgba(97, 126, 88, 0.3)',
        ];

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        // Initial resize
        resize();
        window.addEventListener('resize', resize);

        const handleMouseMove = (event) => {
            mouse.x = event.x;
            mouse.y = event.y;
        };
        window.addEventListener('mousemove', handleMouseMove);

        let globalTime = 0;

        class Particle {
            constructor() {
                this.reset(true);
            }

            reset(initial = false) {
                // Logical X/Y (The "Natural" path)
                this.x = Math.random() * canvas.width;
                if (initial) {
                    this.y = Math.random() * canvas.height;
                } else {
                    this.y = canvas.height + 10;
                    this.x = Math.random() * canvas.width;
                }

                // Interaction Offset (The "Push" distance)
                this.dx = 0;
                this.dy = 0;

                this.size = Math.random() * 2.5 + 0.5;
                this.baseSpeedY = Math.random() * 0.2 + 0.1;
                this.phaseOffset = Math.random() * Math.PI * 2;

                this.color = colors[Math.floor(Math.random() * colors.length)];
            }

            update() {
                // 1. Natural Positioning
                const rhythm = Math.sin(globalTime * 0.02 + this.phaseOffset) * 0.5 + 0.5;
                const currentSpeedY = this.baseSpeedY + (rhythm * 0.3);
                this.y -= currentSpeedY;

                const sway = Math.sin(globalTime * 0.01 + this.y * 0.005) * 0.3;
                this.x += sway;

                // 2. Interaction (Simple Repulsion)
                if (mouse.x != undefined && mouse.y != undefined) {
                    let visualX = this.x + this.dx;
                    let visualY = this.y + this.dy;

                    let distDx = mouse.x - visualX;
                    let distDy = mouse.y - visualY;
                    let distance = Math.sqrt(distDx * distDx + distDy * distDy);

                    if (distance < mouse.radius) {
                        const forceDirectionX = distDx / distance;
                        const forceDirectionY = distDy / distance;
                        const force = (mouse.radius - distance) / mouse.radius;
                        const pushStrength = 4; // Push strength

                        // Push ANY particle within radius away immediately
                        this.dx -= forceDirectionX * force * pushStrength;
                        this.dy -= forceDirectionY * force * pushStrength;
                    }
                }

                // 3. Slow Decay Return (No Spring)
                // Just multiply by fraction < 1. This slides it back to 0 smoothly.
                // 0.98 = Very slow return (Updated from 0.95)
                this.dx *= 0.98;
                this.dy *= 0.98;

                // Wrap around (reset offsets)
                if (this.y < -10) {
                    this.y = canvas.height + 10;
                    this.dx = 0; this.dy = 0;
                }
                if (this.x < -10) this.x = canvas.width + 10;
                if (this.x > canvas.width + 10) this.x = -10;
            }

            draw() {
                ctx.beginPath();
                ctx.fillStyle = this.color;
                ctx.globalAlpha = 0.5;
                ctx.arc(this.x + this.dx, this.y + this.dy, this.size, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        for (let i = 0; i < particleCount; i++) {
            particles.push(new Particle());
        }

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.globalCompositeOperation = 'screen';
            globalTime++;
            particles.forEach(p => {
                p.update();
                p.draw();
            });
            animationFrameId = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resize);
            window.removeEventListener('mousemove', handleMouseMove);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="smoke-particles"
            style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '100vw',
                height: '100vh',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                zIndex: -1 // Behind text
            }}
        />
    );
}
